/**
 * Словари фильтров для Блока 4 «Подбери ручки» — экран 2.4.2.
 *
 * **Источник истины — РЕАЛЬНЫЕ данные каталога** (замер по проду, 5087 SKU,
 * B-220). Списки реконсилированы со значениями полей `color`/`shape`, которые
 * выдаёт импорт (`handle-adapter` → `handle-shape-canon` / `handle-color-canon`).
 * До реконсиляции чипы фильтра не совпадали со значениями в БД и фильтр выдавал
 * пусто (B-220). Чипы рендерятся из этих списков; фильтрация сравнивает значения
 * как есть — без нормализации (P-001).
 *
 * `HANDLE_SHAPES` ≡ выход `canonHandleShape` (14 канон-меток). `HANDLE_COLORS` —
 * наблюдаемые значения `Цветовая группа` после ё-нормализации (`canonHandleColor`):
 * пустой `color` («» в данных, ~1978 SKU) в чипы НЕ входит — он «проходит всегда»
 * (см. `filterHandles`).
 */

import type { HandleKind } from '../contracts/handles';
import { HOOK_COLORS } from './hooks-vocabulary';

export const HANDLE_COLORS = Object.freeze([
  'Хром',
  'Чёрный',
  'Золото',
  'Белый',
  'Светлый никель',
  'Серый',
  'Состаренный металл',
  'Сатин / Никель',
  'Шампань',
  'Латунь',
  'Сталь',
  'Алюминий / Серый',
  'Тёмный никель',
  'Медь / Бронза / Олово',
  'Цветные',
  'Фигуры/Животные',
  'Не указано',
] as const);

export type HandleColor = (typeof HANDLE_COLORS)[number];

/**
 * Подсписок цветов для UI-чипов фильтра «Цвет» (Экран 2.4.2): то же, что
 * `HANDLE_COLORS`, но БЕЗ нефильтруемых значений:
 * - `'Фигуры/Животные'` — это форма (тип товара), переехала в фильтр «Форма» (Этап 3);
 * - `'Не указано'` — не цвет, чип убран по фидбэку Aibek (Этап 3 #1). Поведение
 *   empty-color НЕ меняется: ручки без цвета по-прежнему «проходят всегда» (B-220,
 *   `filterHandles`) — это сугубо состав чипов фильтра.
 * Поле данных `color`, `HANDLE_COLOR_HEX_MAP`, тип `HandleColor` и Block 6
 * (slotResolver) НЕ трогаем.
 */
const NON_FILTERABLE_HANDLE_COLORS: readonly HandleColor[] = ['Фигуры/Животные', 'Не указано'];

export const FILTERABLE_HANDLE_COLORS = Object.freeze(
  HANDLE_COLORS.filter((c) => !NON_FILTERABLE_HANDLE_COLORS.includes(c)),
) as readonly HandleColor[];

/**
 * HEX-карта цветов ручек/крючков — Block 6 Phase 2 Step 2 (ADR-0015 §«Phase 2»).
 *
 * Используется `slotResolver.ts` для рендера зон handles/hooks в SVG-визуализации
 * Экрана 2.6.2: `Handle.color` (категория типа «Хром»/«Латунь») → CSS HEX-строка.
 *
 * Источник истины — `HANDLE_COLORS` (vocabulary вместе со словарём). Покрытие
 * всех ключей (см. `HANDLE_COLORS.length`) + HEX-формат + frozen — locked
 * test'ами в `handles-vocabulary.test.ts`.
 *
 * Approved values (Aibek 09.05.2026): visual hierarchy verified — Хром `C0` >
 * Светлый никель `B0` > Серый `88` > Сталь `71` > Тёмный никель `4A`; Состаренный
 * металл `7B` отличается тёплым brownish тоном; Чёрный `1A` — самый тёмный. Все
 * различимы при рендере 8×2px полоски ручек в SVG.
 *
 * Phase 9+ может расширить (новые цвета через админ-панель) — добавляется и сюда,
 * и в `HANDLE_COLORS` синхронно через ADR-amendment.
 */
export const HANDLE_COLOR_HEX_MAP: Record<HandleColor, string> = Object.freeze({
  Хром: '#C0C0C0',
  Чёрный: '#1a1512',
  Золото: '#D4AF37',
  Белый: '#F5F0E8',
  'Светлый никель': '#B0B0B0',
  Серый: '#888888',
  'Состаренный металл': '#7B6F5C',
  'Сатин / Никель': '#B8B8B8',
  Шампань: '#E2C290',
  Латунь: '#B5A642',
  Сталь: '#71797E',
  'Алюминий / Серый': '#8C8C8C',
  'Тёмный никель': '#4A4A4A',
  'Медь / Бронза / Олово': '#8C6E54',
  Цветные: '#999999',
  'Фигуры/Животные': '#999999',
  'Не указано': '#999999',
});

/**
 * Формы ручек — реконсилированы с реальными данными (B-220): ≡ выход
 * `canonHandleShape`. Порядок ≈ по убыванию частоты в проде (Скоба 1946 …
 * Капля 7). Этап 1: бывшая катч-олл «Комплектующие» разнесена в 5 подгрупп
 * (Прутки ≈83 / Держатели ≈16 / Заглушки ≈13 / Уголки ≈3 / Крепёж-фиксатор ≈1).
 * Старые метки `Скобы`/`Плоские`/`Полукруглые` в данных отсутствуют — убраны.
 */
export const HANDLE_SHAPES = Object.freeze([
  'Скоба',
  'Профильная',
  'Кнопка',
  'Длинные',
  'Полукруглая',
  'Рейлинговая',
  'Накладная',
  'Врезная',
  'Орнамент',
  'Раковина',
  'Плоская',
  'Капля',
  'Гола',
  'Прутки',
  'Держатели',
  'Заглушки',
  'Уголки',
  'Крепёж-фиксатор',
  // Этап 3 (решение Aibek 12.06): «Фигуры/Животные» — это ФОРМА (детские
  // ручки-фигурки: машинка/мяч/тигр/петушок), а не цвет. Импорт переносит её из
  // поля `color` в `shape` (23 SKU, GTV); чип появляется в фильтре «Форма».
  'Фигуры/Животные',
] as const);

export type HandleShape = (typeof HANDLE_SHAPES)[number];

/**
 * Формы-комплектующие ручек (Этап 1: бывшая катч-олл «Комплектующие» разнесена в
 * 5 подгрупп). Единый источник истины для приоритета формы над figure-override
 * (B-248): если «Подкатегория» канонизировалась в одну из этих форм, она ГЛАВНЕЕ
 * метки «Фигуры/Животные» из «Цветовой группы» (соединительный уголок для профиля
 * фальш-ручки — это «Уголки», не детская фигурка). Все значения ∈ `HANDLE_SHAPES`
 * (locked test'ом в `handles-vocabulary.test.ts`).
 */
export const HANDLE_COMPONENT_SHAPES = Object.freeze([
  'Прутки',
  'Держатели',
  'Заглушки',
  'Уголки',
  'Крепёж-фиксатор',
] as const);

/**
 * Бренды ручек/крючков — словарь чипов фильтра «Бренд» (ADR-0076, Этап 3).
 * Реальные бренды каталога (5067 SKU) по убыванию частоты: GTV 1839, Boyard 870,
 * EVA 373, Hafele 40, REJS 9 — плюс «Без бренда» (последним): SKU с пустым
 * `brand` (1936 — плейсхолдеры-магазины «Не известно N»/Аги/Век, ADR-0070).
 * Один список для обоих `kind` (бренд ортогонален kind). Метка «Без бренда»
 * маппится на фильтр-значение `''` через `brandLabelToValue` (P-001 verbatim).
 */
export const NO_BRAND_LABEL = 'Без бренда';

export const HANDLE_BRANDS = Object.freeze([
  'GTV',
  'Boyard',
  'EVA',
  'Hafele',
  'REJS',
  NO_BRAND_LABEL,
] as const);

export type HandleBrandLabel = (typeof HANDLE_BRANDS)[number];

/**
 * Бренд-чипы в разрезе `kind` (доводка Блока 4 после приёмки): показываем только
 * бренды, у которых есть ≥1 товар в этом разделе. По реальным данным каталога
 * (5065 SKU): у РУЧЕК REJS = 0 SKU (REJS-ручки удалены в Этапе 1) — поэтому чип
 * REJS в разделе «Ручки» давал пусто и убран; у КРЮЧКОВ REJS = 9 SKU — остаётся.
 * Прочие бренды (GTV/Boyard/EVA/Hafele/«Без бренда») есть в обоих разделах.
 *
 * Источник тот же, что у остального словаря, — РЕАЛЬНЫЕ данные, ведётся вручную
 * (как `HANDLE_COLORS`/`HANDLE_SHAPES`, B-220). Динамический per-kind список
 * потребовал бы facets-эндпоинта = смены контракта (избегаем). Порядок и
 * «Без бренда» последним наследуются от `HANDLE_BRANDS`.
 */
export const HANDLE_BRANDS_BY_KIND: Record<HandleKind, readonly HandleBrandLabel[]> = Object.freeze(
  {
    handle: HANDLE_BRANDS.filter((b) => b !== 'REJS'),
    hook: HANDLE_BRANDS,
  },
);

/**
 * Цвет-чипы фильтра «Цвет» в разрезе `kind` (B-247). Ведётся вручную — как
 * `HANDLE_BRANDS_BY_KIND`, без facets-эндпоинта и без смены контракта API.
 *
 * По реальным данным каталога у РУЧЕК 0 SKU в трёх «крючковых» цветах
 * («Сатин / Никель», «Алюминий / Серый», «Медь / Бронза / Олово») — эти чипы
 * показывались, но ничего не находили (мёртвые чипы), поэтому убраны из раздела
 * «Ручки». Для КРЮЧКОВ цвет-вокабуляр — `HOOK_COLORS` (реконсилирован с данными,
 * B-220). Значения `HANDLE_COLORS`/`FILTERABLE_HANDLE_COLORS`, `HANDLE_COLOR_HEX_MAP`,
 * тип `HandleColor` и поведение пустого `color` (`filterHandles`) НЕ меняются.
 */
const HANDLE_ONLY_HOOK_COLORS: readonly HandleColor[] = [
  'Сатин / Никель',
  'Алюминий / Серый',
  'Медь / Бронза / Олово',
];

export const HANDLE_COLORS_BY_KIND: Record<HandleKind, readonly HandleColor[]> = Object.freeze({
  handle: FILTERABLE_HANDLE_COLORS.filter((c) => !HANDLE_ONLY_HOOK_COLORS.includes(c)),
  hook: HOOK_COLORS as readonly HandleColor[],
});

/**
 * Метка чипа «Бренд» → значение фильтра `brands[]` для API (ADR-0076).
 * «Без бренда» → `''` (пустой `brand` в данных); реальный бренд → verbatim (P-001).
 */
export function brandLabelToValue(label: string): string {
  return label === NO_BRAND_LABEL ? '' : label;
}
